import React, { useState, useEffect, useCallback } from 'react';
import { useApi } from '@/contexts/AuthContext';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { toast } from 'sonner';
import { AlertOctagon, Plus, CheckCircle2, Clock, Search } from 'lucide-react';

const C = { navy: '#0D1B2A', blue: '#1A4C7C', slate: '#5C6B7A', green: '#1D9E75', red: '#E24B4A', amber: '#E8A020' };
const SEV = { SEV1: C.red, SEV2: '#F0682A', SEV3: C.amber, SEV4: C.slate };
const STATUS = { open: { c: C.red, Icon: AlertOctagon }, investigating: { c: C.amber, Icon: Search }, resolved: { c: C.green, Icon: CheckCircle2 } };

export default function IncidentsPage() {
  const api = useApi();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [form, setForm] = useState({ title: '', severity: 'SEV3', description: '' });

  const load = useCallback(() => {
    return api.get('/compliance/incidents').then(r => setData(r.data)).catch(e => console.error(e?.message)).finally(() => setLoading(false));
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const create = () => {
    if (!form.title.trim()) { toast.error('Title is required'); return; }
    api.post('/compliance/incidents', form)
      .then(() => { toast.success('Incident logged'); setForm({ title: '', severity: 'SEV3', description: '' }); setCreating(false); load(); })
      .catch(e => toast.error(e?.response?.data?.detail || 'Failed'));
  };

  const setStatus = (inc, status) => {
    api.put(`/compliance/incidents/${inc.incident_id}`, { status })
      .then(() => { toast.success(`${inc.incident_id} → ${status}`); load(); })
      .catch(e => toast.error(e?.response?.data?.detail || 'Failed'));
  };

  if (loading) return <div className="space-y-3">{[...Array(3)].map((_, i) => <Skeleton key={i} className="h-20 rounded-md" />)}</div>;

  const incidents = data?.incidents || [];
  const counts = data?.counts || {};

  return (
    <div className="space-y-5" data-testid="incidents-page">
      <div className="flex items-center justify-between">
        <div className="flex gap-3">
          {['open', 'investigating', 'resolved'].map(s => {
            const cfg = STATUS[s];
            return (
              <Card key={s} className="px-4 py-2.5 rounded-md flex items-center gap-2.5" style={{ border: '1px solid rgba(136,150,168,0.14)' }} data-testid={`inc-count-${s}`}>
                <cfg.Icon className="w-4 h-4" style={{ color: cfg.c }} />
                <div><span className="text-lg font-bold font-mono" style={{ color: C.navy }}>{counts[s] || 0}</span><span className="text-xs ml-1.5 capitalize" style={{ color: C.slate }}>{s}</span></div>
              </Card>
            );
          })}
        </div>
        <Button size="sm" onClick={() => setCreating(!creating)} data-testid="new-incident-btn" style={{ background: C.navy, color: 'white' }}>
          <Plus className="w-4 h-4 mr-1.5" />Log Incident
        </Button>
      </div>

      {creating && (
        <Card className="p-4 rounded-md space-y-3" style={{ border: `1px solid ${C.amber}40` }} data-testid="incident-form">
          <Input placeholder="Incident title" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} data-testid="incident-title" />
          <div className="flex gap-2">
            {['SEV1', 'SEV2', 'SEV3', 'SEV4'].map(s => (
              <button key={s} onClick={() => setForm({ ...form, severity: s })} data-testid={`sev-${s}`}
                className="px-3 py-1.5 rounded text-xs font-semibold" style={form.severity === s ? { background: SEV[s], color: 'white' } : { border: `1px solid ${SEV[s]}55`, color: SEV[s] }}>{s}</button>
            ))}
          </div>
          <Textarea placeholder="Description / impact" rows={2} value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} data-testid="incident-desc" />
          <div className="flex justify-end gap-2">
            <Button size="sm" variant="outline" onClick={() => setCreating(false)}>Cancel</Button>
            <Button size="sm" onClick={create} data-testid="save-incident-btn" style={{ background: C.navy, color: 'white' }}>Save</Button>
          </div>
        </Card>
      )}

      {incidents.length === 0 ? (
        <p className="text-sm" style={{ color: C.slate }} data-testid="no-incidents">No incidents logged. Detection signals surface in Admin → System Health monitoring alerts.</p>
      ) : (
        <div className="space-y-2">
          {incidents.map(inc => {
            const st = STATUS[inc.status];
            return (
              <Card key={inc.incident_id} className="px-4 py-3 rounded-md" style={{ border: '1px solid rgba(136,150,168,0.14)' }} data-testid={`incident-${inc.incident_id}`}>
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <span className="text-[10px] font-bold px-2 py-0.5 rounded shrink-0" style={{ background: `${SEV[inc.severity]}1A`, color: SEV[inc.severity] }}>{inc.severity}</span>
                    <span className="text-sm font-medium truncate" style={{ color: C.navy }}>{inc.title}</span>
                  </div>
                  <div className="flex items-center gap-2 shrink-0">
                    <span className="flex items-center gap-1 text-xs capitalize" style={{ color: st.c }}><st.Icon className="w-3.5 h-3.5" />{inc.status}</span>
                    {inc.status !== 'investigating' && inc.status !== 'resolved' && <Button size="sm" variant="outline" className="h-7 text-[11px]" onClick={() => setStatus(inc, 'investigating')} data-testid={`investigate-${inc.incident_id}`}>Investigate</Button>}
                    {inc.status !== 'resolved' && <Button size="sm" className="h-7 text-[11px]" onClick={() => setStatus(inc, 'resolved')} data-testid={`resolve-${inc.incident_id}`} style={{ background: C.green, color: 'white' }}>Resolve</Button>}
                  </div>
                </div>
                {inc.description && <p className="text-[11px] mt-1.5" style={{ color: C.slate }}>{inc.description}</p>}
                <p className="text-[10px] mt-1" style={{ color: C.slate }}>{inc.incident_id} · {inc.created_by} · {new Date(inc.created_at).toLocaleString()}{inc.resolved_at ? ` · resolved ${new Date(inc.resolved_at).toLocaleString()}` : ''}</p>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
